<!--
 * @Author: AI Assistant
 * @Date: 2026-01-17
 * @Description: Recently Used Drawer - 历史记录抽屉组件
 * @FilePath: /weryai-web/components/RecentlyUsedDrawer.vue
-->
<template>
    <Teleport to="body">
        <!-- 遮罩层 -->
        <Transition name="fade">
            <div v-if="visible" class="drawer-overlay" @click="handleClose"></div>
        </Transition>

        <!-- 抽屉面板 -->
        <Transition name="slide">
            <div v-if="visible" class="drawer-panel" :style="{ left: drawerLeft }" @click.stop>
                <!-- 标题区域 -->
                <div class="drawer-header">
                    <div>
                        <h2 class="text-base font-semibold text-white">
                            {{ t('menus.recentlyUsed') }}
                        </h2>
                        <p class="text-xs text-gray-400 mt-1">
                            {{ t('menus.recentlyUsedDesc') }}
                        </p>
                    </div>
                    <!-- 清空按钮 -->
                    <button v-if="recentlyUsedList.length > 0"
                        class="clear-btn text-xs text-gray-400 hover:text-white transition-colors"
                        @click="handleClearAll">
                        {{ t('menus.recentlyUsedClear') }}
                    </button>
                </div>

                <!-- 历史记录列表 -->
                <div class="drawer-content">
                    <!-- 有记录时 -->
                    <template v-if="recentlyUsedList.length > 0">
                        <div class="space-y-2">
                            <div v-for="item in recentlyUsedList" :key="item.id" class="history-card"
                                @click="handleItemClick(item)">
                                <!-- 缩略图/图标 -->
                                <div class="card-thumbnail">
                                    <!-- 定价页面使用金币图片 -->
                                    <img v-if="item.type === 'pricing'" src="~/assets/images/common/coin.webp"
                                        alt="coin" class="w-full h-full object-contain p-2" />
                                    <img v-else-if="item.thumbnail" :src="item.thumbnail" :alt="item.name"
                                        class="w-full h-full object-cover" />
                                    <div v-else class="w-full h-full flex items-center justify-center">
                                        <span class="w-5 h-5 text-gray-400" :class="item.icon || 'i-home-icon'"></span>
                                    </div>
                                </div>

                                <!-- 内容信息 -->
                                <div class="card-content">
                                    <div class="card-title">{{ translateName(item.name) }}</div>
                                    <div v-if="item.description && translateName(item.description) !== translateName(item.name)"
                                        class="card-desc">
                                        {{ translateName(item.description) }}
                                    </div>
                                </div>

                                <!-- 访问时间 -->
                                <div class="card-time">
                                    {{ formatTime(item.visitedAt) }}
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- 无记录时 -->
                    <template v-else>
                        <div class="empty-state">
                            <div class="empty-icon">
                                <span class="i-used-his-icon w-10 h-10 text-gray-600"></span>
                            </div>
                            <p class="text-gray-400 text-sm mt-3">
                                {{ t('menus.recentlyUsedEmpty') }}
                            </p>
                        </div>
                    </template>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { navigateTo } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useAppStore } from '@/stores/app'
import {
    useRecentlyUsed,
    type RecentlyUsedItem,
} from '~/composables/useRecentlyUsed'

const props = defineProps<{
    visible: boolean
}>()

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
    (e: 'close'): void
}>()

const { t } = useI18n()
const appStore = useAppStore()
const { recentlyUsedList, initRecentlyUsed, clearRecentlyUsed } = useRecentlyUsed()

// 计算抽屉位置（基于侧边栏宽度）
const drawerLeft = computed(() => {
    return '10px'
    // return `${appStore.sidebarWidth}px`
})

// 监听 visible 变化，初始化数据
watch(
    () => props.visible,
    newVal => {
        if (newVal) {
            initRecentlyUsed()
        }
    },
    { immediate: true }
)

// 格式化时间显示（支持多语言）
const formatTime = (timestamp: number): string => {
    const now = Date.now()
    const diff = now - timestamp
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
        // 超过1天显示日期
        const date = new Date(timestamp)
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
    }
    if (hours > 0) {
        return t('menus.timeHoursAgo', { n: hours })
    }
    if (minutes > 0) {
        return t('menus.timeMinutesAgo', { n: minutes })
    }
    return t('menus.timeJustNow')
}

// 翻译 supportType 为多语言显示
const translateSupportTypes = (typesStr: string): string => {
    const types = typesStr.split(',')
    const typeKeyMap: Record<string, string> = {
        'text-to-video': 'menus.textToVideo',
        'image-to-video': 'menus.imageToVideo',
        'text-to-image': 'menus.textToImage',
        'image-to-image': 'menus.imageToImage',
    }
    // 英文 fallback
    const fallbackMap: Record<string, string> = {
        'text-to-video': 'Txt2Vid',
        'image-to-video': 'Img2Vid',
        'text-to-image': 'Txt2Img',
        'image-to-image': 'Img2Img',
    }

    return types
        .map(type => {
            const key = typeKeyMap[type]
            if (key) {
                const translated = t(key)
                // 如果翻译成功（不等于原 key），返回翻译结果
                if (translated && translated !== key) {
                    return translated
                }
            }
            // fallback 使用英文
            return fallbackMap[type] || type
        })
        .filter(Boolean)
        .join('、')
}

// 翻译名称（支持翻译 key 格式如 menus.AIVideo）
const translateName = (nameOrKey?: string): string => {
    if (!nameOrKey) return ''

    // 检测 supportTypes 特殊格式：$$SUPPORT_TYPES$$text-to-video,image-to-video
    if (nameOrKey.startsWith('$$SUPPORT_TYPES$$')) {
        const typesStr = nameOrKey.replace('$$SUPPORT_TYPES$$', '')
        return translateSupportTypes(typesStr)
    }

    // 如果是翻译 key 格式（包含点号），尝试翻译
    if (nameOrKey.includes('.')) {
        const translated = t(nameOrKey)
        // 如果翻译成功（不等于原 key），返回翻译结果
        if (translated && translated !== nameOrKey) {
            return translated
        }
    }
    // 否则返回原值
    return nameOrKey
}

// 获取默认描述
const getDefaultDescription = (item: RecentlyUsedItem): string => {
    const typeMap: Record<string, string> = {
        video: t('menus.AIVideo') || 'AI Video',
        image: t('menus.AIImages') || 'AI Image',
        music: t('menus.AIMusic') || 'AI Music',
        character: t('menus.digitalHuman') || 'Characters',
        tool: t('menus.AITools') || 'AI Tools',
        effect: t('menus.effects') || 'Effects',
    }
    return typeMap[item.type || 'other'] || ''
}

// 点击记录项
const handleItemClick = (item: RecentlyUsedItem) => {
    navigateTo(item.path)
    handleClose()
}

// 清空所有记录
const handleClearAll = () => {
    clearRecentlyUsed()
}

// 关闭抽屉
const handleClose = () => {
    emit('update:visible', false)
    emit('close')
}
</script>

<style scoped>
.drawer-overlay {
    @apply fixed inset-0 bg-black/50 z-[100];
}

.drawer-panel {
    @apply fixed top-[2.5%] w-[320px] z-[101];
    @apply flex flex-col shadow-2xl;
    height: 95%;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    background: #1C1C20;
}

.drawer-header {
    @apply flex items-start justify-between p-4 border-b border-white/10;
}

.clear-btn {
    width: 72px;
    height: 30px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
}

.clear-btn:hover {
    background: rgba(255, 255, 255, 0.10);
}

.drawer-content {
    @apply flex-1 overflow-y-auto p-4;
}

.history-card {
    @apply flex items-center gap-3 p-3 cursor-pointer transition-all duration-200;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    background: rgba(255, 255, 255, 0.05);
}

.history-card:hover {
    background: rgba(255, 255, 255, 0.10);
}

.history-card:active {
    @apply scale-[0.99];
}

.card-thumbnail {
    @apply w-12 h-12 overflow-hidden flex-shrink-0;
    @apply flex items-center justify-center;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    background: rgba(0, 0, 0, 0.10);
}

.card-content {
    @apply flex-1 min-w-0;
}

.card-title {
    @apply text-white text-sm font-medium truncate;
}

.card-desc {
    @apply text-xs text-gray-400 truncate mt-0.5;
}

.card-time {
    @apply text-xs text-gray-500 flex-shrink-0;
}

.empty-state {
    @apply flex flex-col items-center justify-center py-16;
}

.empty-icon {
    @apply w-16 h-16 rounded-full bg-[#26262a] flex items-center justify-center;
}

/* 过渡动画 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* 打开时有滑动动画 */
.slide-enter-active {
    transition: transform 0.25s ease;
}

/* 关闭时直接消失，不要滑动动画 */
.slide-leave-active {
    transition: none;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}

/* 图标样式 */
.i-used-his-icon {
    mask: url('~/assets/images/menu/usedHis.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask: url('~/assets/images/menu/usedHis.svg') no-repeat center;
    -webkit-mask-size: contain;
    background-image: none;
    background-color: currentColor;
}

.i-home-icon {
    mask: url('~/assets/images/menu/home.png') no-repeat center;
    mask-size: contain;
    -webkit-mask: url('~/assets/images/menu/home.png') no-repeat center;
    -webkit-mask-size: contain;
    background-image: none;
    background-color: currentColor;
}

.i-ai-chat-icon {
    mask: url('~/assets/images/menu/ai-chat.svg') no-repeat center;
    mask-size: contain;
    -webkit-mask: url('~/assets/images/menu/ai-chat.svg') no-repeat center;
    -webkit-mask-size: contain;
    background-image: none;
    background-color: currentColor;
}
</style>
